import type { ContactContent } from '@mawkaa/types';
import { Button, Input, Textarea, Label } from '@mawkaa/ui';
import { Mail, Phone, MapPin, MessageCircle } from 'lucide-react';

interface ContactBlockProps {
  content: ContactContent;
  isEditing?: boolean;
}

export function ContactBlock({ content, isEditing }: ContactBlockProps) {
  const {
    title,
    subtitle,
    showForm = true,
    formFields = [],
    showInfo = true,
    email,
    phone,
    whatsapp,
    address,
  } = content;

  return (
    <section className="px-4 py-16">
      <div className="max-w-6xl mx-auto">
        {(title || subtitle) && (
          <div className="text-center mb-12">
            {title && <h2 className="text-3xl font-bold mb-4">{title}</h2>}
            {subtitle && <p className="text-lg text-muted-foreground">{subtitle}</p>}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact form */}
          {showForm && (
            <div>
              <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
                {formFields.map((field, index) => (
                  <div key={index} className="space-y-2">
                    <Label htmlFor={field.name}>
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </Label>
                    {field.type === 'textarea' ? (
                      <Textarea
                        id={field.name}
                        name={field.name}
                        placeholder={`Votre ${field.label.toLowerCase()}`}
                        required={field.required}
                        disabled={isEditing}
                      />
                    ) : (
                      <Input
                        id={field.name}
                        name={field.name}
                        type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
                        placeholder={`Votre ${field.label.toLowerCase()}`}
                        required={field.required}
                        disabled={isEditing}
                      />
                    )}
                  </div>
                ))}
                <Button type="submit" className="w-full" disabled={isEditing}>
                  Envoyer le message
                </Button>
              </form>
            </div>
          )}

          {/* Contact info */}
          {showInfo && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold mb-4">Nos coordonnées</h3>

              {email && (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Email</p>
                    <a href={`mailto:${email}`} className="text-muted-foreground hover:text-primary">
                      {email}
                    </a>
                  </div>
                </div>
              )}

              {phone && (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                    <Phone className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Téléphone</p>
                    <a href={`tel:${phone}`} className="text-muted-foreground hover:text-primary">
                      {phone}
                    </a>
                  </div>
                </div>
              )}

              {whatsapp && (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center shrink-0">
                    <MessageCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">WhatsApp</p>
                    <a
                      href={`https://wa.me/${whatsapp.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-green-600"
                    >
                      {whatsapp}
                    </a>
                  </div>
                </div>
              )}

              {address && (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Adresse</p>
                    <p className="text-muted-foreground">{address}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
